import { useCallback, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Routes } from '@/core/router/routes.types';
import { cookieLocation, cookieLocationBrandGuest } from '@/hooks/useAuthCookieHandler/useAuthCookieHandler';
import { useLazyBrandGuestLoginQuery } from '@/services/innovorder/auth/auth.endpoints';
import { AuthUser } from '@/services/innovorder/auth/auth.type';
import { UserRoles } from '@/services/innovorder/user/user.type';
import { setBrandFilter } from '@/redux/analyticsFilters';
import { updateUser } from '@/redux/user';

export const computeCookieDomain = (host: string): string | undefined => {
    switch (true) {
        case host.includes('.innovorder.io'):
            return '.innovorder.io';
        case host.includes('.innovorder.fr'):
            return '.innovorder.fr';
        default:
            return undefined;
    }
};

export const COOKIE_PATH = '/';

type AuthSuccessError = 'no_brands_configured' | 'guest_brand_login_failed';

type UseAuthSuccessResult = {
    handleAuthSuccess: (user: AuthUser, accessToken: string) => void;
    error: AuthSuccessError | null;
    clearError: () => void;
};

export const useAuthSuccess = (): UseAuthSuccessResult => {
    const dispatch = useDispatch();
    const history = useHistory();
    const [, setCookies] = useCookies([cookieLocation]);
    const [brandGroupContext, setBrandGroupContext] = useState<{
        brandGroupId: number;
        mainUserEmail: string;
    } | null>(null);
    const [error, setError] = useState<AuthSuccessError | null>(null);

    const [
        loginAsGuestBrand,
        { data: guestBrandLoginData, isSuccess: guestBrandLoginSuccess, isError: guestBrandLoginIsError },
    ] = useLazyBrandGuestLoginQuery();

    const clearError = useCallback(() => setError(null), []);

    const handleAuthSuccess = useCallback(
        (user: AuthUser, accessToken: string) => {
            setCookies(
                cookieLocation,
                { accessToken },
                { domain: computeCookieDomain(window.location.host), path: COOKIE_PATH },
            );
            dispatch(updateUser({ ...user, isGuest: false, accessToken }));

            if (user.role !== UserRoles.admin && user.brands?.length > 0) {
                dispatch(setBrandFilter(user.brands[0]));
            }
            if (user.role === UserRoles.brandGroup) {
                if (!user.brands || user.brands.length === 0) {
                    setError('no_brands_configured');
                    return;
                }
                setBrandGroupContext({
                    brandGroupId: user.brandGroupId,
                    mainUserEmail: user.email,
                });
                loginAsGuestBrand({ brandId: user.brands[0].brandId });
                return;
            }
            history.push(Routes.Analytics);
        },
        [dispatch, history, loginAsGuestBrand, setCookies],
    );

    useEffect(() => {
        if (guestBrandLoginSuccess && guestBrandLoginData && brandGroupContext) {
            setCookies(
                cookieLocationBrandGuest,
                {
                    accessToken: guestBrandLoginData.accessToken,
                    brandGroupId: brandGroupContext.brandGroupId,
                    mainUserEmail: brandGroupContext.mainUserEmail,
                },
                { domain: computeCookieDomain(window.location.host), path: COOKIE_PATH },
            );
            history.push(Routes.Analytics);
        }
    }, [guestBrandLoginSuccess, guestBrandLoginData, brandGroupContext, history, setCookies]);

    useEffect(() => {
        if (guestBrandLoginIsError) setError('guest_brand_login_failed');
    }, [guestBrandLoginIsError]);

    return { handleAuthSuccess, error, clearError };
};
